import { FaMoneyBillWave, FaCoins } from 'react-icons/fa';
import { useSmartDropdownEvent } from '../service/Hooks/useSmartDropdownEvent';

/**
 * COMPOSANT DROPDOWN DE DISTRIBUTION INTELLIGENT
 * Utilise useSmartDropdownEvent pour un positionnement et affichage optimaux
 * @param {Object} props
 * @param {Function} props.onDistributeClick - Callback appelé quand l'utilisateur clique sur Distribuer le Cash
 * @param {Object} props.event - L'événement à distribuer
 */
const DistributionDropdown = ({ onDistributeClick, event }) => {
  // Utilisation du hook intelligent pour le dropdown
  const {
    isOpen,
    position,
    triggerRef,
    dropdownRef,
    toggleDropdown,
    closeDropdown
  } = useSmartDropdownEvent();

  /**
   * Gère le clic sur le bouton principal
   * Logique différente selon le type d'événement
   */
  const handleMainButtonClick = () => {
    if (event.type === 'Non Cash') {
      // Pour Non Cash: ouverture directe de la modal de confirmation
      onDistributeClick(event, null);
    } else {
      // Pour Dividend to be paid ou Capital repayment: afficher le dropdown
      toggleDropdown();
    }
  };

  /**
   * Gère la sélection d'un mode de distribution
   * @param {boolean} withTax - Si le mode avec impôt est sélectionné
   */
  const handleModeSelect = (withTax = false) => {
    closeDropdown();
    
    if (event.type === 'Non Cash') {
      // Non Cash: pas de paramètres d'impôt
      onDistributeClick(event, null);
    } else {
      // Dividend to be paid ou Capital repayment: on passe le flag forTax
      onDistributeClick(event, withTax);
    }
  };

  /**
   * Détermine la configuration du bouton selon le type d'événement
   * @returns {Object} Configuration avec texte, icône et couleur
   */
  const getButtonConfig = () => {
    switch (event.type) {
      case 'Non Cash':
        return {
          text: 'Distribuer les Titres',
          icon: <FaCoins className="me-1" />,
          color: 'primary',
          bgColor: '#4b6cb7',
          gradient: 'linear-gradient(135deg, #4b6cb7 0%, #182848 100%)'
        };
      case 'Dividend to be paid':
        return {
          text: 'Distribuer le Cash',
          icon: <FaMoneyBillWave className="me-1" />,
          color: 'success',
          bgColor: '#28a745',
          gradient: 'linear-gradient(135deg,  #4b6cb7 0%, #182848 100%)'
        };
      case 'Capital repayment':
        return {
          text: 'Distribuer le Capital',
          icon: <FaMoneyBillWave className="me-1" />,
          color: 'warning',
          bgColor: '#ffc107',
          gradient: 'linear-gradient(135deg,  #4b6cb7 0%, #182848 100%)'
        };
      default:
        return {
          text: 'Distribuer',
          icon: null,
          color: 'secondary',
          bgColor: '#6c757d',
          gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'
        };
    }
  };

  // Récupération de la configuration du bouton
  const buttonConfig = getButtonConfig();

  return (
    <div className={`custom-distribution-dropdown ${isOpen ? 'is-open' : ''}`}>
      {/* Overlay pour capturer les clics en dehors (uniquement quand le dropdown est ouvert) */}
      {isOpen && event.type !== 'Non Cash' && (
        <div 
          className="dropdown-overlay" 
          onClick={closeDropdown}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent'
          }}
        />
      )}
      
      {/* Bouton principal déclencheur */}
      <button
        ref={triggerRef}
        onClick={handleMainButtonClick}
        className="d-flex align-items-center gap-2 px-3 py-2"
        style={{
          background: buttonConfig.gradient,
          border: 'none',
          borderRadius: '8px',
          fontWeight: '600',
          fontSize: '14px',
          color: 'white',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          minWidth: '160px',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          position: 'relative',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
        }}
      >
        {buttonConfig.icon}
        <span>{buttonConfig.text}</span>
        {event.type !== 'Non Cash' && (
          <span className="ms-1" style={{ fontSize: '10px' }}>▼</span>
        )}
      </button>

      {/* Menu déroulant avec positionnement intelligent (uniquement pour Dividend/Capital) */}
      {event.type !== 'Non Cash' && isOpen && (
        <div
          ref={dropdownRef}
          className="dropdown-menu-container"
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 1001,
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-10px)',
            transition: 'all 0.2s ease',
            transformOrigin: 'top left',
            pointerEvents: isOpen ? 'auto' : 'none'
          }}
          hidden={!isOpen}
        >
          <div 
            className="dropdown-menu-custom shadow-lg"
            style={{ 
              minWidth: '280px', 
              padding: '12px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Option: Distribuer sans impôt */}
            <div
              onClick={() => handleModeSelect(false)}
              className="dropdown-item-custom d-flex align-items-center gap-3 p-3 rounded-2 mb-2"
              style={{ 
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                background: 'rgba(40, 167, 69, 0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(40, 167, 69, 0.1)';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(40, 167, 69, 0.05)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div className="bg-success bg-opacity-10 p-2 rounded-circle flex-shrink-0">
                <FaMoneyBillWave className="text-success" size={18} />
              </div>
              <div className="flex-grow-1">
                <div className="fw-semibold text-dark">Distribuer sans impôt</div>
                <div className="text-muted small mt-1">
                  Confirmer la distribution sans prélever d'impôt
                </div>
              </div>
            </div>

            {/* Option: Distribuer avec impôt */}
            <div
              onClick={() => handleModeSelect(true)}
              className="dropdown-item-custom d-flex align-items-center gap-3 p-3 rounded-2"
              style={{ 
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                background: 'rgba(13, 110, 253, 0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(13, 110, 253, 0.1)';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(13, 110, 253, 0.05)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div className="bg-primary bg-opacity-10 p-2 rounded-circle flex-shrink-0">
                <FaMoneyBillWave className="text-primary" size={18} />
              </div>
              <div className="flex-grow-1">
                <div className="fw-semibold text-dark">Distribuer avec impôt</div>
                <div className="text-muted small mt-1">
                  Saisir un montant d'impôt optionnel à prélever
                </div>
              </div>
            </div>

            {/* Info sur le type d'événement */}
            <div 
              className="mt-3 pt-3 border-top text-center"
              style={{ borderColor: 'rgba(0, 0, 0, 0.05)' }}
            >
              <small className="text-muted">
                Type: <span className="fw-semibold">{event.type}</span>
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DistributionDropdown;